import type { DateRange } from 'react-day-picker'

import { apiClientV2 } from '@/utils/axios'
import { dateToString } from '@/utils/date-converter.ts'

import type {
  SocialAccount,
  SocialAccountResponse,
  SocialAccountVideosResponse,
  SocialMediaType,
  SocialUpdateResponse,
  SocialVideoDownloadResponse,
} from '../types'

/** Query */

export const getSocialAccountsByDate = async (
  creatorId: number,
  socialMediaType: SocialMediaType,
  fromDate: string,
  toDate: string,
): Promise<SocialAccountResponse> => {
  const response = await apiClientV2.get(
    `${creatorId}/${socialMediaType}/accounts`,
    {
      params: {
        from: fromDate,
        to: toDate,
      },
    },
  )

  return response.data
}

export const getSocialAccountsByCreatorId = async (
  creatorId: number | undefined,
  socialMediaType: SocialMediaType,
): Promise<SocialAccount[]> => {
  const response = await apiClientV2.get(`${creatorId}/${socialMediaType}`)

  return response.data
}

/** Update */

export const deleteSocialAccountByCreatorId = async (
  creatorId: number | undefined,
  socialMediaType: SocialMediaType,
  accountId: string,
): Promise<void> => {
  const response = await apiClientV2.delete(`${creatorId}/${socialMediaType}`, {
    params: {
      id: accountId,
    },
  })

  return response.data
}

export const addSocialAccount = async (
  creatorId: number | undefined,
  socialMediaType: SocialMediaType,
  accountUsername: string,
): Promise<SocialUpdateResponse> => {
  const response = await apiClientV2.post(`${creatorId}/${socialMediaType}`, {
    name: accountUsername,
  })

  return response.data
}

export const downloadSocialVideo = async (
  creatorId: number | undefined,
  socialMediaType: SocialMediaType,
  accountUsername: string,
  shortId: string,
): Promise<SocialVideoDownloadResponse> => {
  const response = await apiClientV2.get(
    `${creatorId}/${socialMediaType}/videos/download?`,
    {
      params: {
        accountName: accountUsername,
        shortId,
        creatorId,
        type: socialMediaType,
      },
    },
  )

  return response.data
}

/**
 * Pagination
 */

export const fetchVideos = async (
  creatorId: number | undefined,
  socialMediaType: SocialMediaType,
  accountName: string,
  page: number,
  sortOption: string,
  { from, to }: DateRange,
): Promise<SocialAccountVideosResponse> => {
  const fromDate = dateToString(from)
  const toDate = dateToString(to)

  const response = await apiClientV2.get(
    `${creatorId}/${socialMediaType}/videos`,
    {
      params: {
        page: page,
        accountName: accountName,
        from: fromDate,
        to: toDate,
        sortBy: sortOption,
      },
    },
  )

  return response.data
}
